import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Alert, Badge, Button, Group, Modal, Paper, Stack, Text, Title } from '@mantine/core'
import {
  UnsavedChangesGuard,
  newRacePlan,
  planTimesProblem,
  racePlanKey,
  type RacePlanDoc,
} from '@racedash/shared'
import { useShell } from '../AppShell'
import { useDrivers } from '../team/api'
import { useAttachedEvents } from '../dashboard/useDashboardSource'
import {
  createRacePlan,
  deleteRacePlan,
  saveRacePlan,
  useRacePlans,
  useSessions,
} from '../racePlan/api'
import RacePlanEditor from '../racePlan/RacePlanEditor'
import FuelPlanSection from '../racePlan/FuelPlanSection'

/**
 * Planning a weekend: one plan per race, each with its own rules and windows.
 *
 * A weekend with two races is two plans, and neither depends on a timetable
 * existing. Where one does, it fills in names and times with a click; where it
 * does not — which is most events until a few days beforehand — they are typed.
 * Nothing here breaks in the absence of a timetable, because a plan that could
 * not be made without one would be unusable exactly when it was most wanted.
 */
export default function RacePlan() {
  const { orgId, eventId } = useParams()
  const { currentOrg } = useShell()
  const { events } = useAttachedEvents(orgId)
  const { plans, error } = useRacePlans(orgId, eventId)
  const sessions = useSessions(eventId)
  const { items: drivers } = useDrivers(orgId)

  const event = (events ?? []).find((e) => e.eventId === eventId)


  const [planId, setPlanId] = useState<string | null>(null)
  const stored = (plans ?? []).find((p) => p.id === planId) ?? null

  useEffect(() => {
    if (planId || !plans?.length) return
    setPlanId(plans[0]?.id ?? null)
  }, [plans, planId])

  const baseline: RacePlanDoc = useMemo(
    () => stored ?? newRacePlan(eventId ?? '', 'Race'),
    [stored, eventId],
  )
  const [draft, setDraft] = useState<RacePlanDoc>(baseline)

  /*
   * The drivers entered at this event, not the whole club roster.
   *
   * A rotation is an order for the people who are actually here. Offering
   * thirty names when three of them are at the circuit makes building one a
   * search instead of a glance, and makes it possible to roster somebody who is
   * two hundred miles away.
   *
   * The union across the team's entries rather than one car's, because a plan
   * belongs to the event and a team running two cars shares a squad between
   * them. Falling back to the whole roster where the entries name nobody, since
   * an empty picker is worse than a long one — the same rule the screens use.
   */
  const planDrivers = useMemo(() => {
    const all = (drivers ?? []).map((d) => ({ id: d.id, name: d.name }))
    const entered = new Set((event?.entries ?? []).flatMap((e) => e.driverIds ?? []))
    if (entered.size === 0) return all
    // Anyone already in the rotation stays offered, whatever the entry says. A
    // list that narrowed under a plan somebody had already built would render
    // their choices as "Unknown" and lose them on the next edit.
    for (const id of draft.drivers) entered.add(id)
    const narrowed = all.filter((d) => entered.has(d.id))
    return narrowed.length > 0 ? narrowed : all
  }, [event, drivers, draft.drivers])
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [switchTo, setSwitchTo] = useState<string | null>(null)

  /*
   * Adopt what the server says, but only while there is nothing of our own to
   * lose — the same rule the dashboard's screens follow.
   *
   * Keyed on what the plan says rather than on its timestamp. The timestamp is
   * not read back, so an effect keyed on it never ran after the first render,
   * and a comparison of the objects as they stood saw key order and called a
   * saved plan unsaved.
   */
  const baselineKey = racePlanKey(baseline)
  const lastBaseline = useRef<{ planId: string | null; key: string } | null>(null)
  useEffect(() => {
    const prev = lastBaseline.current
    lastBaseline.current = { planId, key: baselineKey }
    setDraft((d) =>
      !prev || prev.planId !== planId || racePlanKey(d) === prev.key ? baseline : d,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, baselineKey])

  const dirty = racePlanKey(draft) !== baselineKey
  const timesProblem = planTimesProblem(draft)

  /** Another plan's tab, asking first where this one has unsaved edits. */
  const choose = (id: string) => {
    if (id === planId) return
    if (dirty) setSwitchTo(id)
    else setPlanId(id)
  }

  const save = async () => {
    if (!orgId || !planId || timesProblem) return
    setBusy(true)
    setFailed(null)
    try {
      await saveRacePlan(orgId, planId, draft)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    if (!orgId || !eventId) return
    setBusy(true)
    setFailed(null)
    try {
      const name = `Race ${(plans?.length ?? 0) + 1}`
      setPlanId(await createRacePlan(orgId, eventId, name))
    } catch (e) {
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!currentOrg) return <Text c="dimmed">Organisation not found.</Text>

  return (
    <Stack gap="lg">
      <UnsavedChangesGuard
        when={dirty}
        message="This race plan has not been saved. Leaving will discard your changes."
      />

      <Modal opened={switchTo != null} onClose={() => setSwitchTo(null)} title="Switch without saving?">
        <Stack gap="md">
          <Text size="sm">
            {draft.name} has not been saved. Switching to another race will discard your changes.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSwitchTo(null)}>
              Stay on {draft.name}
            </Button>
            <Button
              color="red"
              onClick={() => {
                setPlanId(switchTo)
                setSwitchTo(null)
              }}
            >
              Discard changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1} size="h2">
            Race plans
          </Title>
          <Text c="dimmed" size="sm">
            {event?.name ?? 'Event'} — the rules you are running under, when you may stop, and who
            drives. One plan per race.
          </Text>
        </div>
        <Button component={Link} to={`/o/${orgId}/events`} variant="default">
          Back to events
        </Button>
      </Group>

      {error && <Alert color="red">{error}</Alert>}
      {failed && (
        <Alert color="red" title="Not saved">
          {failed}
        </Alert>
      )}

      <Paper withBorder p="sm">
        <Group gap="xs" wrap="wrap">
          {(plans ?? []).map((p) => (
            <Button
              key={p.id}
              size="compact-sm"
              variant={p.id === planId ? 'filled' : 'default'}
              onClick={() => choose(p.id)}
            >
              {p.name}
              {p.startsAt != null && (
                <Text component="span" size="xs" ml={6} c={p.id === planId ? undefined : 'dimmed'}>
                  {new Date(p.startsAt).toLocaleString(undefined, {
                    weekday: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}
            </Button>
          ))}
          <Button size="compact-sm" variant="light" loading={busy} onClick={() => void add()}>
            + New race
          </Button>
        </Group>
      </Paper>

      {plans?.length === 0 && (
        <Text c="dimmed" size="sm">
          No plans yet. Add one for each race you are running — a name and a start time is enough
          to begin with.
        </Text>
      )}

      {stored && (
        <>
          <Group gap="xs">
            <Badge variant="light" color={draft.regime.windows?.entries.length ? 'green' : 'gray'}>
              {draft.regime.windows?.entries.length
                ? `${draft.regime.windows.entries.length} windows`
                : 'no windows set'}
            </Badge>
            {draft.startsAt == null && (
              <Badge variant="light" color="yellow">
                no start time
              </Badge>
            )}
          </Group>

          <RacePlanEditor
            plan={draft}
            sessions={sessions ?? []}
            event={event ? { name: event.name, startsAt: event.startsAt, endsAt: event.endsAt } : undefined}
            drivers={planDrivers}
            onChange={setDraft}
          />

          <FuelPlanSection plan={draft} orgId={orgId} onChange={setDraft} />

          <Group justify="space-between">
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              onClick={() => {
                if (!orgId || !planId) return
                setPlanId(null)
                void deleteRacePlan(orgId, planId)
              }}
            >
              Delete this plan
            </Button>
            <Group>
              {/* Where the plan stands, always, so a save that worked is
                  visibly different from one that did nothing. */}
              <Text size="sm" c={timesProblem ? 'red' : dirty ? 'yellow.7' : 'dimmed'}>
                {timesProblem
                  ? `${timesProblem} Fix the times to save.`
                  : dirty
                    ? 'Unsaved changes'
                    : 'All changes saved'}
              </Text>
              <Button variant="default" disabled={!dirty} onClick={() => setDraft(baseline)}>
                Revert
              </Button>
              <Button
                disabled={!dirty || timesProblem != null}
                loading={busy}
                onClick={() => void save()}
              >
                Save plan
              </Button>
            </Group>
          </Group>
        </>
      )}
    </Stack>
  )
}
